import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { ListResponse } from '../../types'
import type { Inspection } from './types'
import { INSPECTION_OUTCOMES } from './types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CloseIcon, CheckIcon, PlusIcon } from '../../components/icons'

export default function GovInspections() {
  const qc = useQueryClient()
  const [outcome, setOutcome] = useState('')
  const [showForm, setShowForm] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'inspections', outcome],
    queryFn: () => api.get<ListResponse<Inspection>>(
      `/gov/inspections${outcome ? `?outcome=${encodeURIComponent(outcome)}` : ''}`,
    ),
  })

  const record = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/gov/inspections', body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['gov'] }); setShowForm(false) },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    record.mutate({
      org_id: f.get('org_id'),
      site_id: f.get('site_id') || '',
      inspection_date: f.get('inspection_date'),
      outcome: f.get('outcome'),
      findings: f.get('findings'),
      recommendations: f.get('recommendations') || '',
      follow_up_date: f.get('follow_up_date') || null,
    })
  }

  const items = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Inspections</h1>
          <p className="page-head__sub">Record site visits and their findings.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Record inspection'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Record inspection'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Record an inspection"
                      sub="Region and district default to your assigned area." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {record.isError && <Alert tone="danger">{(record.error as Error).message}</Alert>}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Where', content: (
                      <div className="grid grid--2">
                        <Input label="Cooperative ID" name="org_id" required
                               placeholder="Organisation inspected" />
                        <Input label="Site ID (optional)" name="site_id"
                               placeholder="Specific site, if applicable" />
                      </div>
                    ) },
                    { title: 'Visit & outcome', content: (
                      <div className="grid grid--2">
                        <Input label="Inspection date" name="inspection_date" type="date" required />
                        <Select label="Outcome" name="outcome" required defaultValue="compliant">
                          {INSPECTION_OUTCOMES.map((o) => (
                            <option key={o} value={o}>{titleCase(o)}</option>
                          ))}
                        </Select>
                        <Input label="Follow-up date (optional)" name="follow_up_date" type="date" />
                      </div>
                    ) },
                    { title: 'Findings', content: (
                      <div className="stack">
                        <Textarea label="Findings" name="findings" required
                                  placeholder="What was observed during the visit." />
                        <Textarea label="Recommendations" name="recommendations"
                                  placeholder="What the cooperative should do next." />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={record.isPending}>Save inspection</Button>
                      <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button size="sm" variant={outcome === '' ? 'primary' : 'secondary'}
                onClick={() => setOutcome('')}>All</Button>
        {INSPECTION_OUTCOMES.map((o) => (
          <Button key={o} size="sm" variant={outcome === o ? 'primary' : 'secondary'}
                  onClick={() => setOutcome(o)}>
            {titleCase(o)}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader title="Records"
                    sub={items.length ? `${items.length} inspection${items.length === 1 ? '' : 's'}` : undefined} />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack">
              <Skeleton h={20} /><Skeleton h={20} w="70%" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState icon={<CheckIcon size={22} />} title="No inspections"
                        body="Inspections you record will appear here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Date</th><th>Cooperative</th><th>Outcome</th>
                      <th>Findings</th><th>Follow-up</th></tr>
                </thead>
                <tbody>
                  {items.map((ins, i) => (
                    <tr key={ins.id ?? i}>
                      <td>
                        {ins.id
                          ? <Link to={`/inspections/${ins.id}`}>{formatDate(ins.inspection_date ?? ins.date)}</Link>
                          : formatDate(ins.inspection_date ?? ins.date)}
                      </td>
                      <td>{ins.org_id || '—'}</td>
                      <td><StatusBadge status={ins.outcome} /></td>
                      <td style={{ maxWidth: 320 }}>{ins.findings || '—'}</td>
                      <td>{ins.follow_up_date ? formatDate(ins.follow_up_date) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
